import { useDraftStore } from "@/store/draft";
import { useTrackingStore } from "@/store/tracking";
import { useCartStore } from "@/store/cart";
import { useOrderHistoryStore } from "@/store/orderHistory";
import { constants } from "@/constants";
import Orion from "@/services/orion";
import CartService from "@/services/cart";
import CouponService from "@/services/coupon";
import Logger from "@/services/logger";
import Redirect from "@/services/redirect";
import Pg from "@/services/pg";
import Utils from "@/services/utils";
import Navigation from "@/services/navigation";
import DuplicateOrderGuard from "@/services/duplicateOrderGuard";
import OneClickLoader from "@/services/payment/oneClickLoader";
import { stampConfirmationOfferWindow } from "@/services/confirmationOfferWindow";

const OrderMethod = {
  ONE_CLICK: "oneclick",
};

const URL_COUPON_TIMEOUT_MS = 8000;

export default class OneClickService {
  static URL_COUPON_TIMEOUT_MS = URL_COUPON_TIMEOUT_MS;

  static shouldHandleOneClick() {
    const currentUrlParams = new URLSearchParams(window.location.search);
    const orderMethod = currentUrlParams.get("orderMethod");
    const addChoices = currentUrlParams.get("addChoices");
    const urlParams = useTrackingStore().urlParams;
    const shouldHandle =
      orderMethod === OrderMethod.ONE_CLICK &&
      !!urlParams.sfId &&
      !!urlParams.walletId &&
      !!addChoices;
    console.log("[OneClick] shouldHandleOneClick:", shouldHandle, {
      orderMethod,
      sfId: urlParams.sfId,
      walletId: urlParams.walletId,
      addChoices,
    });

    if (shouldHandle) {
      const url = new URL(window.location.href);
      url.searchParams.delete("orderMethod");
      url.searchParams.delete("addChoices");
      window.history.replaceState({}, "", url.toString());

      useTrackingStore().deleteParams(["orderMethod", "addChoices"]);

      console.log(
        "[OneClick] orderMethod and addChoices params removed from URL and store",
      );

      return addChoices;
    }

    return null;
  }

  static getForcedPaymentType() {
    const params = new URLSearchParams(window.location.search);
    const paymentType = (params.get("paymentType") || "").toLowerCase();

    if (paymentType === "sepa" || paymentType === "bank_card") {
      return paymentType;
    }

    return null;
  }

  static async processOneClick(addChoices) {
    console.log("[OneClick] processOneClick: starting");
    useDraftStore().setOneClickProcessing(true);
    OneClickLoader.start();
    try {
      const urlParams = useTrackingStore().urlParams;

      const alreadyClicked = await Orion.is1ClickLinkAlreadyUsed();
      console.log("[OneClick] alreadyClicked:", alreadyClicked.status);
      if (alreadyClicked.status) {
        console.log("[OneClick] processOneClick: link already used, aborting");
        this.handleAlreadyClicked(addChoices);
        return false;
      }

      let prefillData;
      try {
        console.log("[OneClick] processOneClick: fetching prefill data");
        prefillData = await Pg.getPrefillData(
          urlParams.sfId,
          window.globalConfig.affiliateId,
        );
        if (!prefillData?.billingEmail) {
          console.log("[OneClick] processOneClick: no prefill data found");
          return false;
        }
        console.log("[OneClick] processOneClick: prefill data received");
      } catch (error) {
        console.error(
          "[OneClick] processOneClick: failed to fetch prefill data",
          error,
        );
        return false;
      }

      const productDetails = window.productDetails;
      if (!productDetails?.choices) {
        console.log("[OneClick] processOneClick: no product details");
        return false;
      }

      const shippingCountry =
        prefillData.shippingCountry || prefillData.billingCountry;
      if (shippingCountry) {
        useDraftStore().selectCountryCode(shippingCountry);
      }

      const choiceIds = [...new Set(addChoices.split(","))];
      const choices = [];
      for (const choiceId of choiceIds) {
        const choice = productDetails.choices.find((c) => c.id === choiceId);
        if (!choice) {
          console.log(
            `[OneClick] processOneClick: choice ${choiceId} not found`,
          );
          return false;
        }
        choices.push(choice);
      }

      // The order must contain exactly the link's offers, in quantity 1:
      // the order page may already have added them from `addChoices`, and a
      // saved cart may hold other offers.
      CartService.emptyCart();
      console.log(
        "[OneClick] processOneClick: adding choices to cart",
        choiceIds,
      );

      for (const choice of choices) {
        const productToAdd = { ...productDetails };
        productToAdd.choice = choice;
        productToAdd.qtyIncr = 1;
        CartService.addToCart(productToAdd, { skipUrlCoupons: true });
      }

      const expectedCoupons = CouponService.parseUrlCoupons();
      const couponAbort =
        typeof AbortController !== "undefined" ? new AbortController() : null;
      let couponTimeoutId;
      let couponFailure = null;
      try {
        await Promise.race([
          CouponService.applyCouponsFromUrl({ signal: couponAbort?.signal }),
          new Promise((_, reject) => {
            couponTimeoutId = setTimeout(() => {
              couponAbort?.abort();
              reject(new Error("coupon check timed out"));
            }, OneClickService.URL_COUPON_TIMEOUT_MS);
          }),
        ]);
      } catch (error) {
        couponFailure = error;
        console.error(
          "[OneClick] processOneClick: coupon application failed",
          error,
        );
      } finally {
        clearTimeout(couponTimeoutId);
      }

      const cartCoupons = useCartStore().cart?.coupons || {};
      const missingCoupons = expectedCoupons.filter(
        (code) => !cartCoupons[code],
      );
      if (missingCoupons.length) {
        Logger.error("[OneClick] order sent without URL coupon", {
          coupon: urlParams.coupon,
          missing: missingCoupons,
          choices: choiceIds,
          reason: couponFailure?.message || "coupon not applied",
        });
      }

      const paymentMethod = prefillData.oneClickSEPA
        ? constants.PAYMENT_METHOD_SEPA
        : constants.PAYMENT_METHOD_BANK_CARD;
      useDraftStore().selectPaymentMethod(paymentMethod);
      console.log(
        "[OneClick] processOneClick: payment method set to",
        paymentMethod,
      );

      const order = {
        externalOrderId: Utils.generateUID(),
        billingEmail: prefillData.billingEmail,
        billingFirstName: prefillData.billingFirstName,
        billingLastName: prefillData.billingLastName,
        billingPhoneNumber: prefillData.billingPhoneNumber,
        billingGender: prefillData.billingGender,
        billingStreet1: prefillData.billingStreet1,
        billingStreet2: prefillData.billingStreet2,
        billingStreet3: prefillData.billingStreet3,
        billingStreet4: prefillData.billingStreet4,
        billingZipCode: prefillData.billingZipCode,
        billingCity: prefillData.billingCity,
        billingCountry: prefillData.billingCountry,
        shippingFirstName:
          prefillData.shippingFirstName || prefillData.billingFirstName,
        shippingLastName:
          prefillData.shippingLastName || prefillData.billingLastName,
        shippingPhoneNumber:
          prefillData.shippingPhoneNumber || prefillData.billingPhoneNumber,
        shippingGender: prefillData.shippingGender || prefillData.billingGender,
        shippingStreet1:
          prefillData.shippingStreet1 || prefillData.billingStreet1,
        shippingStreet2:
          prefillData.shippingStreet2 || prefillData.billingStreet2,
        shippingStreet3:
          prefillData.shippingStreet3 || prefillData.billingStreet3,
        shippingStreet4:
          prefillData.shippingStreet4 || prefillData.billingStreet4,
        shippingZipCode:
          prefillData.shippingZipCode || prefillData.billingZipCode,
        shippingCity: prefillData.shippingCity || prefillData.billingCity,
        shippingCountry:
          prefillData.shippingCountry || prefillData.billingCountry,
        sameAsBilling: true,
        acceptCgv: true,
        acceptCgs: true,
      };

      useDraftStore().approveCgs(true);
      useDraftStore().approveCgv(true);

      console.log("[OneClick] processOneClick: preparing order");
      const preparedOrder = Orion.prepareOrder(
        useCartStore().cart,
        order,
        { orderType: "initial" },
        true,
      );
      preparedOrder.orderMethod = OrderMethod.ONE_CLICK;

      const minifiedCart = CartService.minifyCart();

      console.log("[OneClick] processOneClick: sending order to Orion");
      try {
        await Orion.sendOrder(
          minifiedCart,
          preparedOrder,
          window.globalConfig.domainId,
          "createOneClick",
        );
      } catch (error) {
        console.error(
          "[OneClick] processOneClick: failed to send order",
          error,
        );
        return false;
      }

      useOrderHistoryStore().addOrderToHistory(
        useCartStore().cart,
        preparedOrder,
        0,
      );

      if (paymentMethod === constants.PAYMENT_METHOD_SEPA) {
        console.log(
          "[OneClick] processOneClick: SEPA payment, redirecting to confirmation",
        );
        const paramPlus = this.buildParamPlus(preparedOrder);
        localStorage.setItem(constants.PARAM_PLUS, JSON.stringify(paramPlus));
        Redirect.redirectToConfirmation(paramPlus);
      } else {
        console.log(
          "[OneClick] processOneClick: Bank card payment, redirecting to waiting page",
        );
        const paramPlus = this.buildParamPlus(preparedOrder);
        localStorage.setItem(constants.PARAM_PLUS, JSON.stringify(paramPlus));
        window.location.href = `${window.location.origin}${window.globalConfig.waitingPage}`;
      }

      return true;
    } catch (error) {
      console.error("[OneClick] processOneClick: unexpected error", error);
      return false;
    } finally {
      this.stopOneClickProcessing();
    }
  }

  static buildParamPlus(preparedOrder) {
    const paramPlus = {};

    // FORGE-308: parsing robuste via URLSearchParams (gère valeurs avec `=`,
    // encodage URL, clés sans valeur). Filtrage des params preview/test pour
    // ne pas polluer le flow paiement (cf. constants.IGNORED_PARAM_PLUS_KEYS).
    const queryParams = Navigation.getQueryParams();
    for (const [key, value] of queryParams) {
      if (!constants.IGNORED_PARAM_PLUS_KEYS.includes(key)) {
        paramPlus[key] = value;
      }
    }

    paramPlus.orderId = preparedOrder.externalOrderId;
    paramPlus.paymentType = preparedOrder.paymentType;
    paramPlus.action = "capture";
    paramPlus.authorizationType = "capture_now";
    paramPlus.retryUrl = `${window.location.origin}${window.location.pathname.replace(/\/$/, "")}?retry=${preparedOrder.externalOrderId}`;

    return stampConfirmationOfferWindow(paramPlus);
  }

  static stopOneClickProcessing() {
    useDraftStore().setOneClickProcessing(false);
    OneClickLoader.stop();
  }

  static handleAlreadyClicked(addChoices = "") {
    this.stopOneClickProcessing();

    console.log("[OneClick] handleAlreadyClicked: start");

    const choiceIds = addChoices
      .split(",")
      .map((choiceId) => choiceId.trim())
      .filter(Boolean);
    const blockedChoiceIds = new Set(choiceIds);
    const blockedSalesforceIds = new Set();

    for (const choiceId of choiceIds) {
      const choice = window.productDetails?.choices?.find(
        (item) => item.id === choiceId,
      );
      const productSalesforceId = choice?.salesforceProductId;
      if (productSalesforceId) {
        blockedSalesforceIds.add(productSalesforceId);
      }
    }

    const cartItems = useCartStore().cart?.items || [];
    const cartItemsToRemove = choiceIds.length
      ? cartItems.filter((item) => choiceIds.includes(item?.choice?.id))
      : cartItems.slice(0, 1);

    for (const cartItem of cartItemsToRemove) {
      console.log(
        "[OneClick] handleAlreadyClicked: removing cart item",
        cartItem,
      );
      CartService.deleteItem(cartItem);

      if (cartItem?.choice?.id) {
        blockedChoiceIds.add(String(cartItem.choice.id));
      }

      const choiceSalesforceId = cartItem.choice?.salesforceProductId;
      if (choiceSalesforceId) {
        blockedSalesforceIds.add(choiceSalesforceId);
      }
    }

    DuplicateOrderGuard.handleDuplicateBlocked(useCartStore().cart, {
      reason: "already_clicked",
      blockedChoiceIds: Array.from(blockedChoiceIds),
      blockedProductIds: Array.from(blockedSalesforceIds),
    });
    console.log(
      "[OneClick] handleAlreadyClicked: redirecting to already-subscribed flow",
    );
  }

  static prefillOneClick(data) {
    if (
      window.globalConfig.authorized1click &&
      !data.doNotSend1CLICKOffers &&
      (data.oneClickCC || data.oneClickSEPA)
    ) {
      const forcedPaymentType = OneClickService.getForcedPaymentType();
      const paymentType =
        forcedPaymentType || (data.oneClickSEPA ? "sepa" : "bank_card");
      useDraftStore().selectPaymentMethod(paymentType);
      useDraftStore().eligibleToOneClick(true);
      useDraftStore().updateActionLegacy("createOneClick");
      useDraftStore().updateFullAddress(
        `${data.shippingStreet3}, ${data.shippingZipCode}, ${data.shippingCity}`,
      );

      // Sur order page, l'utilisateur doit cocher CGV affilie + CGS Palyance
      // avant de debloquer le bouton 1-click (meme logique que l'iframe).
      if (window.globalConfig?.isOrderPage) {
        useDraftStore().formValid(false);
        useDraftStore().approveCgs(false);
        useDraftStore().approveCgv(false);
        return {};
      }

      useDraftStore().formValid(true);
      useDraftStore().approveCgs(true);
      useDraftStore().approveCgv(true);
      return {
        acceptCgs: true,
        acceptCgv: true,
      };
    }
    return {};
  }

  static isEligibleToOneClick() {
    return (
      useDraftStore().isEligibleToOneClick &&
      window.globalConfig.authorized1click
    );
  }
}
